import type { SelectChangeEvent } from "@mui/material";
import { creditorTypeColumns, denominationColumns } from "./data";
import type { ClauseRow, NodeKind } from "./types";

export const parseNode = (node: string): { kind: NodeKind; value: string } => {
  if (node.startsWith("CR: ")) {
    return { kind: "creditorRegion", value: node.replace("CR: ", "") };
  }
  if (node.startsWith("BR: ")) {
    return { kind: "borrowerRegion", value: node.replace("BR: ", "") };
  }
  if (creditorTypeColumns.includes(node)) {
    return { kind: "creditorType", value: node };
  }
  if (denominationColumns.includes(node)) {
    return { kind: "denomination", value: node };
  }
  return { kind: "other", value: node };
};

export const handleMultiSelect = (
  event: SelectChangeEvent<string[]>,
  setter: (next: string[]) => void,
) => {
  const value = event.target.value;
  setter(typeof value === "string" ? value.split(",") : value);
};

export const formatBillions = (value: number) =>
  `${(value / 1_000_000_000).toFixed(1)}B`;

export const isCompleteDataFalse = (
  value: boolean | string | null | undefined,
) => {
  if (value === false) return true;
  if (typeof value !== "string") return false;

  return value.trim().toLowerCase() === "false";
};

// Clause helpers

export function cleanGroup(raw: string | null | undefined): string {
  return String(raw ?? "")
    .replace(/^"|"$/g, "")
    .trim();
}

export function cleanAvailability(raw: string | null | undefined): string {
  return String(raw ?? "")
    .replace(/^"|"$/g, "")
    .trim();
}

// Count row occurrences per category value for a given field
export function countByField(
  rows: ClauseRow[],
  field: keyof ClauseRow,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const v = String(row[field] ?? "").trim();
    if (!v) continue;
    map.set(v, (map.get(v) ?? 0) + 1);
  }
  return map;
}

// Count unique DOCIDs per category value for a given fields
export function docsByField(
  rows: ClauseRow[],
  field: keyof ClauseRow,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const row of rows) {
    const v = String(row[field] ?? "").trim();
    const docId = String(row.DOCID ?? "").trim();
    if (!v || !docId) continue;
    if (!map.has(v)) map.set(v, new Set());
    map.get(v)!.add(docId);
  }
  return map;
}
